// Enhanced version of dateTimeUtils.js
export const validateDateTime = (fromDate, fromHour, fromMinute, toDate, toHour, toMinute, logistics) => {
    const now = new Date();
    const fromDateTime = new Date(`${fromDate}T${fromHour}:${fromMinute}:00`);
    const toDateTime = new Date(`${toDate}T${toHour}:${toMinute}:00`);

    // Reset seconds and milliseconds for more accurate comparison
    now.setSeconds(0);
    now.setMilliseconds(0);

    // Basic validation
    if (fromDateTime < now) {
        return {
            isValid: false,
            error: 'Hire from date and time must be in the future'
        };
    }

    if (toDateTime <= fromDateTime) {
        return {
            isValid: false,
            error: 'Hire to date and time must be after the hire from date and time'
        };
    }

    const hoursDifference = (toDateTime - fromDateTime) / (1000 * 60 * 60);
    if (hoursDifference < 1) {
        return {
            isValid: false,
            error: 'Minimum hire period is 1 hour'
        };
    }

    // Collection restrictions - check for ANY weekend
    const isCollection = logistics === 'Collect' || logistics === 'Collection';

    if (isCollection) {
        const fromDay = fromDateTime.getDay();

        // Check if it's a weekend day (Saturday = 6, Sunday = 0)
        if (fromDay === 0 || fromDay === 6) {
            return {
                isValid: false,
                error: 'Collection is not available on weekends. Please select a weekday between 8:30 am and 5:30 pm.'
            };
        }

        // Check office hours only on weekdays
        const fromHours = fromDateTime.getHours();
        const fromMinutes = fromDateTime.getMinutes();
        const isBeforeOfficeHours = fromHours < 8 || (fromHours === 8 && fromMinutes < 30);
        const isAfterOfficeHours = fromHours > 17 || (fromHours === 17 && fromMinutes > 30);

        if (isBeforeOfficeHours || isAfterOfficeHours) {
            return {
                isValid: false,
                error: 'Collection is only available during office hours: 8:30 am to 5:30 pm on weekdays.'
            };
        }
    }

    return {
        isValid: true,
        error: null
    };
};

// Function to adjust dates based on business rules
export const adjustDateTimeForBusinessRules = (dateTime, logistics) => {
    const date = new Date(dateTime);
    const day = date.getDay(); // 0 is Sunday, 6 is Saturday
    const hours = date.getHours();
    const minutes = date.getMinutes();

    let adjustedDate = new Date(date);

    // For collection
    if (logistics === 'Collect' || logistics === 'Collection') {
        // Check if weekend day (ANY weekend, not just the closest)
        if (day === 0 || day === 6) {
            // If Saturday, move to next Monday 8:30 AM
            if (day === 6) {
                adjustedDate.setDate(date.getDate() + 2); // Move to Monday
                adjustedDate.setHours(8, 30, 0, 0);
            }
            // If Sunday, move to next Monday 8:30 AM 
            else {
                adjustedDate.setDate(date.getDate() + 1); // Move to Monday
                adjustedDate.setHours(8, 30, 0, 0);
            }
        } else {
            // If outside office hours on weekday
            const isBeforeOfficeHours = hours < 8 || (hours === 8 && minutes < 30);
            const isAfterOfficeHours = hours > 17 || (hours === 17 && minutes > 30);

            if (isBeforeOfficeHours) {
                // Set to 8:30 AM same day
                adjustedDate.setHours(8, 30, 0, 0);
            } else if (isAfterOfficeHours) {
                // Set to 8:30 AM next day
                adjustedDate.setDate(date.getDate() + 1);
                adjustedDate.setHours(8, 30, 0, 0);

                // If next day is Saturday, move to Monday
                if (adjustedDate.getDay() === 6) {
                    adjustedDate.setDate(adjustedDate.getDate() + 2);
                }
            }
        }
    }

    return adjustedDate;
};

export const getMinDateTime = () => {
    const now = new Date();
    // Round up to the next 5 minutes
    const minutes = Math.ceil(now.getMinutes() / 5) * 5;
    now.setMinutes(minutes);
    now.setSeconds(0);
    now.setMilliseconds(0);

    return {
        date: now.toISOString().split('T')[0],
        hour: now.getHours().toString().padStart(2, '0'),
        minute: now.getMinutes().toString().padStart(2, '0')
    };
};

// Helper to check if a specific date is within office hours
export const isWithinOfficeHours = (date) => {
    const day = date.getDay();
    const hours = date.getHours();
    const minutes = date.getMinutes();

    // Weekend check
    if (day === 0 || day === 6) return false;

    // Office hours check (8:30 AM to 5:30 PM)
    const isBeforeOfficeHours = hours < 8 || (hours === 8 && minutes < 30);
    const isAfterOfficeHours = hours > 17 || (hours === 17 && minutes > 30);

    return !isBeforeOfficeHours && !isAfterOfficeHours;
};

// Get next available business date/time
export const getNextAvailableBusinessDateTime = () => {
    const now = new Date();
    const day = now.getDay();
    const hours = now.getHours();
    const minutes = now.getMinutes();

    let nextAvailable = new Date(now);

    // If weekend
    if (day === 0 || day === 6) {
        // Calculate days to add to get to Monday
        const daysToAdd = day === 0 ? 1 : 2;
        nextAvailable.setDate(now.getDate() + daysToAdd);
        nextAvailable.setHours(8, 30, 0, 0);
    }
    // If weekday but outside office hours
    else {
        const isBeforeOfficeHours = hours < 8 || (hours === 8 && minutes < 30);
        const isAfterOfficeHours = hours > 17 || (hours === 17 && minutes > 30);

        if (isBeforeOfficeHours) {
            // Set to 8:30 AM same day
            nextAvailable.setHours(8, 30, 0, 0);
        } else if (isAfterOfficeHours) {
            // Set to 8:30 AM next day
            nextAvailable.setDate(now.getDate() + 1);
            nextAvailable.setHours(8, 30, 0, 0);

            // If next day is Saturday, move to Monday
            if (nextAvailable.getDay() === 6) {
                nextAvailable.setDate(nextAvailable.getDate() + 2);
            }
        }
    }

    return {
        date: nextAvailable.toISOString().split('T')[0],
        hour: nextAvailable.getHours().toString().padStart(2, '0'),
        minute: nextAvailable.getMinutes().toString().padStart(2, '0')
    };
};
